import type {
  ContentReplaceModifier,
  CustomModifier,
  MockResponseModifier,
  TransactionModifier,
} from '@carboncopyco/types';
import { encodeToBase64 } from '../base64';
import { getContentType, getContentTypeCategory } from '../http';

type CreateStaticModifier = {
  response: string;
} & Pick<
  MockResponseModifier['arguments'],
  'headers' | 'representationType' | 'representationFields'
>;

type CreateContentReplaceModifier = {
  match: string;
  replace: string;
  applyTo?: ContentReplaceModifier['applyTo'];
};

type Modifiers = {
  static: CreateStaticModifier;
  dynamic: CreateContentReplaceModifier;
  custom: CustomModifier;
};

type BuilderFunc<T, K> = (values: T) => K;
type ModifierBuilder = {
  static: BuilderFunc<Modifiers['static'], MockResponseModifier>;
  dynamic: BuilderFunc<Modifiers['dynamic'], ContentReplaceModifier>;
  custom: BuilderFunc<Modifiers['custom'], CustomModifier>;
};

export const modifierBuilder: ModifierBuilder = {
  static: (values: CreateStaticModifier) => createStaticModifier(values),
  dynamic: ({ match, replace }: CreateContentReplaceModifier) =>
    createContentReplaceModifier({ match, replace }),
  custom: (values) => createCustomModifier(values),
};

type CreateModifiers = Pick<Modifiers, 'static' | 'custom'> & {
  dynamic: CreateContentReplaceModifier[];
};

export const createModifiers = <T extends keyof Modifiers>(
  type: T,
  values: CreateModifiers[T]
): TransactionModifier[] => {
  if (type === 'static' && 'response' in values) {
    return [modifierBuilder.static(values)];
  }

  if (type === 'dynamic' && Array.isArray(values)) {
    return values.map(({ match, replace }) =>
      modifierBuilder.dynamic({ match, replace })
    );
  }

  if (type === 'custom' && 'type' in values) {
    return [modifierBuilder.custom(values)];
  }

  return [];
};

const createStaticModifier = ({
  response,
  headers = [],
  representationType,
  representationFields,
}: CreateStaticModifier): MockResponseModifier => {
  const isImage = getContentTypeCategory(getContentType(headers)) === 'image';
  return {
    type: 'MockResponse',
    applyTo: 'response',
    arguments: {
      content: isImage ? response : encodeToBase64(response),
      contentEncoding: 'base64',
      headers: headers.map(({ name, value }) => ({
        name: name.toLowerCase(),
        value,
      })),
      representationType,
      representationFields,
    },
  };
};

const createContentReplaceModifier = ({
  applyTo = 'response',
  match,
  replace,
}: CreateContentReplaceModifier): ContentReplaceModifier => {
  return {
    type: 'ContentReplace',
    applyTo,
    arguments: {
      match,
      replace,
      mode: 'simple',
      caseSensitive: false,
    },
  };
};
const createCustomModifier = ({
  applyTo,
  type,
  arguments: args,
}: CustomModifier): CustomModifier => {
  return {
    type,
    applyTo,
    arguments: args,
  };
};
